import MasterCrudPage from "../../components/MasterCrudPage";
import {
  addressMasterApi,
  orderMasterApi,
  orderStatusMasterApi,
  serviceApi,
  userMasterApi,
  userRoleApi,
} from "../../api/masterApi";
import {
  filterBuyerUsers,
  filterSellerUsers,
  userDisplayName,
} from "../../utils/userRoleFilters";

const currencies = [
  { value: "INR", label: "INR - Indian Rupee" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "AED", label: "AED - UAE Dirham" },
  { value: "AUD", label: "AUD - Australian Dollar" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "SGD", label: "SGD - Singapore Dollar" },
];

function addressLabel(address) {
  const parts = [
    address.address_line_1,
    address.city_name,
    address.postal_code,
  ].filter(Boolean);
  return parts.join(", ") || "Address";
}

export default function OrderMaster() {
  return (
    <MasterCrudPage
      title="Order Master"
      description="Manage marketplace orders between buyers and sellers."
      idKey="order_id"
      api={orderMasterApi}
      listLabel="orders"
      useJson
      loadExtraOptions={async () => {
        const [users, roles, assets, addresses, statuses] = await Promise.all([
          userMasterApi.list(),
          userRoleApi.list(),
          serviceApi.list(),
          addressMasterApi.list(),
          orderStatusMasterApi.list(),
        ]);

        const buyers = filterBuyerUsers(users, roles);
        const sellers = filterSellerUsers(users, roles);

        return {
          buyers: buyers.map((user) => ({
            value: user.user_id,
            label: userDisplayName(user),
          })),
          sellers: sellers.map((user) => ({
            value: user.user_id,
            label: userDisplayName(user),
          })),
          assets: (assets || []).map((asset) => ({
            value: asset.service_id,
            label: asset.service_title || "Asset",
          })),
          sellerAddresses: (addresses || []).map((address) => ({
            value: address.address_id,
            label: addressLabel(address),
            parentId: address.user_id,
          })),
          buyerAddresses: (addresses || []).map((address) => ({
            value: address.address_id,
            label: addressLabel(address),
            parentId: address.user_id,
          })),
          orderStatuses: (statuses || []).map((status) => ({
            value: status.order_status_id,
            label: status.order_status_name,
          })),
        };
      }}
      fields={[
        {
          name: "order_datetime",
          label: "Date & Time",
          type: "datetime-local",
        },
        {
          name: "buyer_user_id",
          label: "Buyer User",
          type: "select",
          required: true,
          optionsKey: "buyers",
          placeholder: "Select buyer",
        },
        {
          name: "seller_user_id",
          label: "Seller User",
          type: "select",
          required: true,
          optionsKey: "sellers",
          placeholder: "Select seller",
        },
        {
          name: "asset_id",
          label: "Asset",
          type: "select",
          required: true,
          optionsKey: "assets",
          placeholder: "Select asset",
        },
        {
          name: "quantity",
          label: "Quantity",
          type: "number",
          required: true,
          step: "0.01",
          min: "0",
          defaultValue: "1",
          placeholder: "1",
        },
        {
          name: "currency",
          label: "Currency",
          type: "select",
          options: currencies,
          placeholder: "Select currency",
        },
        {
          name: "unit_price",
          label: "Unit Price in Selected Currency",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "discount",
          label: "Discount in Selected Currency",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "amount",
          label: "Amount",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "dispatch_address_id",
          label: "Dispatch Address (Seller)",
          type: "select",
          optionsKey: "sellerAddresses",
          filterBy: "seller_user_id",
          placeholder: "Select seller address",
        },
        {
          name: "delivery_address_id",
          label: "Delivery Address (Buyer)",
          type: "select",
          optionsKey: "buyerAddresses",
          filterBy: "buyer_user_id",
          placeholder: "Select buyer address",
        },
        {
          name: "shipment_tracking_id",
          label: "Shipment Tracking",
          type: "text",
          placeholder: "Enter tracking",
        },
        {
          name: "order_status_id",
          label: "Order Status",
          type: "select",
          optionsKey: "orderStatuses",
          placeholder: "Select order status",
        },
        {
          name: "alternate_mobile",
          label: "Alternate Mobile",
          type: "text",
          placeholder: "Enter alternate mobile",
        },
      ]}
      columns={[
        { key: "order_id", label: "Order" },
        {
          key: "order_datetime",
          label: "Date & Time",
          render: (item) =>
            item.order_datetime
              ? String(item.order_datetime).replace("T", " ").slice(0, 16)
              : "—",
        },
        {
          key: "buyer_user_id",
          label: "Buyer",
          render: (item) =>
            [item.buyer_first_name, item.buyer_last_name].filter(Boolean).join(" ") ||
            "—",
        },
        {
          key: "seller_user_id",
          label: "Seller",
          render: (item) =>
            [item.seller_first_name, item.seller_last_name].filter(Boolean).join(" ") ||
            "—",
        },
        {
          key: "asset_id",
          label: "Asset",
          render: (item) => item.service_title || "—",
        },
        { key: "quantity", label: "Qty" },
        { key: "currency", label: "Currency" },
        { key: "unit_price", label: "Unit Price" },
        { key: "discount", label: "Discount" },
        { key: "amount", label: "Amount" },
        {
          key: "dispatch_address_id",
          label: "Dispatch Address",
          render: (item) => item.dispatch_address_line || "—",
        },
        {
          key: "delivery_address_id",
          label: "Delivery Address",
          render: (item) => item.delivery_address_line || "—",
        },
        { key: "shipment_tracking_id", label: "Shipment Tracking" },
        {
          key: "order_status_id",
          label: "Status",
          render: (item) => item.order_status_name || "—",
        },
        { key: "alternate_mobile", label: "Alternate Mobile" },
      ]}
    />
  );
}
